<!--
// app/components/pages/region-teams/CardTeam.vue
-->
<script setup lang="ts">
import type { Team } from "#shared/team/domain/models/Team";
import type { Staff } from "#shared/team/domain/models/Staff";
import { useTeams } from "~/composables/teams";
import { useRegions } from "~/composables/regions";
import { useImages } from "~/composables/images";
import { setAliasUrl } from "#shared/Shared/utils";
import nuxtLink from "#app/components/nuxt-link";

type Props = {
  toTeamPage: string;
  team: Team;
  preload?: boolean;
  hasLeagueTitle?: boolean;
  isWildcardTeam?: boolean;
};

const props = defineProps<Props>();

const { findTeamPresident } = useTeams();
const { isAmericas, isWorldCup, isNations } = useRegions();
const { playerPlaceholderPath } = useImages();

const defaultColor = "#888888";

const teamPresident = findTeamPresident(props.team);

const findPresidentImage = (president: Staff): string | undefined => {
  if (president) {
    if (president.image && president.image?.url) {
      return president.image?.url;
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

const firstColorHex = props.team.firstColorHEX ? props.team.firstColorHEX : defaultColor;
const secondColorHex = props.team.secondColorHEX ? props.team.secondColorHEX : defaultColor;
</script>

<template>
  <div class="team-card-container">
    <component :is="toTeamPage ? nuxtLink : 'div'" class="h-full w-full" :to="toTeamPage">
      <div class="flex justify-between items-center h-full relative z-[3]">
        <div
          class="clip-diagonal-right"
          :style="`background-image: linear-gradient(180deg, ${
            firstColorHex
          }99 30%, ${secondColorHex}99 90%);`"
        />
        <div
          class="gradient-team-color-hover"
          :style="`background-image: linear-gradient(0deg, ${firstColorHex}10 0%, rgb(20,20,20) 100%);`"
        />
        <div
          v-if="!isWorldCup || (isWorldCup && !isWildcardTeam)"
          class="clip-diagonal-left"
          :style="`background-image: linear-gradient(0deg, ${firstColorHex}20 0%, ${secondColorHex}20 100%);`"
        />
        <div
          v-else-if="isWorldCup && isWildcardTeam"
          :class="['clip-diagonal-left', { 'opacity-30': team.metaInformation?.flag_team_url }]"
          :style="
            team.metaInformation?.flag_team_url
              ? `background-image: url('${team.metaInformation.flag_team_url}'); background-position: center center; background-size: cover;`
              : `background-image: linear-gradient(0deg, ${firstColorHex}20 0%, ${secondColorHex}20 100%);`
          "
        />
      </div>

      <div class="team-card-content">
        <div class="container-logo-img">
          <NuxtImg
            v-if="team?.logo?.url"
            :src="setAliasUrl(team.logo?.url)"
            :alt="team.completeName || '-'"
            :preload="preload"
            class="object-contain"
            width="100"
            height="100"
            sizes="69px sm:100px"
          />
          <div v-else class="bg-gray-800 font-bold text-xl rounded-full py-5 px-7">?</div>
        </div>

        <div :class="['container-team-info', !isAmericas && !isNations ? '-ms-12' : '-ms-14 sm:-ms-2']">
          <div class="container-president-img">
            <img
              v-if="!teamPresident"
              src="/img/placeholder/kl-player-placeholder.png"
              alt="Unknown president"
              :width="isNations ? 270 : 235"
              height="150"
              :sizes="isNations ? '220px sm:270px' : '220px sm:235px'"
              :class="[
                'max-h-[150px] object-top object-contain',
                !isAmericas && !isNations ? 'max-w-[120%]' : 'max-w-full',
              ]"
            />
            <NuxtImg
              v-else
              :src="setAliasUrl(findPresidentImage(teamPresident)) || playerPlaceholderPath"
              :alt="teamPresident?.shortName || ''"
              :preload="preload"
              fit="outside"
              :width="isNations ? 270 : 235"
              height="150"
              :sizes="isNations ? '220px sm:270px' : '220px sm:235px'"
              :class="[
                'max-h-[150px] object-top object-contain',
                !isAmericas && !isNations ? 'max-w-[120%]' : 'max-w-full',
              ]"
            />
          </div>

          <div class="container-team-name-text">
            <component :is="hasLeagueTitle ? 'h3' : 'h2'" class="team-name">
              {{ team.completeName }}
            </component>
            <p class="team-president">
              {{ !!teamPresident ? teamPresident.shortName : "" }}
            </p>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<style scoped>
.team-card-container {
  @apply rounded-lg bg-gray-900 relative h-[172px] overflow-hidden;
}

.clip-diagonal-right {
  @apply h-full w-[133px] relative z-[3];
  clip-path: polygon(0 0, 100% 0%, 35% 100%, 0% 100%);
}

.clip-diagonal-right:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 65% 100%);
}

.clip-diagonal-right::after {
  content: "";
  @apply w-full h-2 absolute -bottom-8 bg-white bg-opacity-50 shadow-md shadow-[#FFFFFF50] blur-sm;
  transition:
    height 250ms ease-in-out,
    bottom 300ms ease-in-out;
}

.clip-diagonal-left {
  @apply h-full w-[73px] translate-x-2 rtl:-translate-x-2 bg-opacity-20 z-[2];
  clip-path: polygon(100% 0, 100% 0%, 100% 100%, 0% 100%);
}

.clip-diagonal-left:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(0 0, 0 0, 100% 100%, 0 100%);
}

.gradient-team-color-hover {
  @apply absolute top-0 right-0 left-0 bottom-0 translate-x-full rtl:-translate-x-full z-[2];
}

.team-card-content {
  @apply flex justify-start items-start h-full w-full absolute top-0 right-0 bottom-0 left-0 z-[5];
}

.container-logo-img {
  @apply flex justify-center items-center p-2 min-h-[85px] min-w-[85px] max-w-[85px] max-h-[85px] h-[85px] md:min-h-[100px] md:min-w-[100px] md:max-w-[100px] md:max-h-[100px] md:h-[100px];
}

.container-logo-img img,
.container-logo-img div {
  @apply transition-transform ease-in-out duration-300 origin-top-left;
}

.container-team-info {
  @apply flex justify-start gap-3 items-end h-full pt-4 md:-ms-8 pe-2 sm:pe-4;
}

.container-president-img {
  @apply sm:shrink-0 max-w-[65%] sm:max-w-full;
}

.container-president-img img {
  @apply transition-transform ease-in-out duration-300 origin-bottom;
}

.container-team-name-text {
  @apply flex flex-col justify-center items-start h-full pb-4 -ms-3 w-[110px] sm:w-auto;
}

.team-name {
  @apply text-gray-300 text-base md:text-xl font-bold;
}

.team-president {
  @apply text-gray-400 text-sm leading-[21px];
}

.clip-diagonal-right,
.clip-diagonal-left,
.gradient-team-color-hover,
.container-team-name-text {
  @apply transition-all ease-in-out duration-200;
}

.team-card-container:hover div.clip-diagonal-right {
  @apply w-[140px];
  clip-path: polygon(0 0, 100% 0%, 38% 100%, 0% 100%);
}

.team-card-container:hover div.clip-diagonal-right:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 62% 100%);
}

.team-card-container:hover .clip-diagonal-right::after {
  @apply bottom-[105%] h-4;
}

.team-card-container:hover div.clip-diagonal-left {
  @apply w-[83px] translate-x-0;
  clip-path: polygon(80% 0, 100% 0%, 100% 100%, 0% 100%);
}

.team-card-container:hover div.clip-diagonal-left:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(0 0, 20% 0, 100% 100%, 0 100%);
}

.team-card-container:hover .gradient-team-color-hover {
  @apply translate-x-0;
}

.team-card-container:hover .container-logo-img img,
.team-card-container:hover .container-logo-img div,
.team-card-container:hover .container-president-img img {
  @apply scale-110;
}

.team-card-container:hover .container-team-name-text {
  @apply ms-1;
}
</style>
